import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import Paper from "@mui/material/Paper";
import Select from "@mui/material/Select";
import { styled } from "@mui/material/styles";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TablePagination from "@mui/material/TablePagination";
import TableRow from "@mui/material/TableRow";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { HttpStatusCode } from "axios";
import PaginationParameters from "_shared/_api/queries/pagination/pagination-parameters";
import { getApiErrorStatus } from "_shared/_errors/api-error-message";
import React, { useEffect, useMemo, useState } from "react";
import { EmailLogAPIError, useSearchEmailLogQuery } from "users/super-users/emails/_api/email-log-api";
import type { EmailLogOutcome } from "users/super-users/emails/_schemas/email-log-entry-schema";
import type { EmailLogEntrySummary } from "users/super-users/emails/email-log-entry";
import EmailLogEntryDialog from "users/super-users/_views/emails/email-log-entry-dialog";
import { formatEmailLogInstant, startOfUtcDay } from "users/super-users/_views/emails/email-log-format";

export const EMAIL_LOG_SEARCH_DELAY_MS = 300;
// An email sent while the tab is open appears within seconds, without a reload (#6922). Only while
// the window has focus, so a tab left open in the background sends nothing.
export const EMAIL_LOG_POLLING_INTERVAL_MS = 10_000;
export const EMAIL_LOG_SEARCH_MAXIMUM_LENGTH = 320;
export const EMAIL_LOG_REFUSED = "You do not have permission to read the email log.";
export const EMAIL_LOG_FAILED = "Unable to load the email log.";
export const EMAIL_LOG_CAPTURED_NOTICE =
  "Captured emails were recorded instead of sent: their environment does not send email, so nobody received them.";
const ROWS_PER_PAGE_OPTIONS = [10, 25, 50, 100];
const OUTCOMES: EmailLogOutcome[] = ["Sent", "Captured", "Failed"];
const OUTCOME_COLORS: Record<EmailLogOutcome, "success" | "info" | "error"> = {
  Sent: "success",
  Captured: "info",
  Failed: "error",
};

const Page = styled(Paper)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(2),
  marginTop: theme.spacing(2),
  padding: theme.spacing(2),
}));
const FilterBar = styled("div")(({ theme }) => ({
  display: "grid",
  gap: theme.spacing(1.5),
  gridTemplateColumns: "repeat(auto-fit, minmax(12rem, 1fr))",
}));
const ClickableRow = styled(TableRow)({
  cursor: "pointer",
});

type EmailLogFilters = {
  recipient: string;
  template: string;
  outcome: EmailLogOutcome | "";
  fromDay: string;
  toDay: string;
};
const NO_FILTERS: EmailLogFilters = { recipient: "", template: "", outcome: "", fromDay: "", toDay: "" };

function searchErrorMessage(error: unknown): string {
  const response = error instanceof EmailLogAPIError ? error.error : error;
  return getApiErrorStatus(response) === HttpStatusCode.Forbidden ? EMAIL_LOG_REFUSED : EMAIL_LOG_FAILED;
}

/**
 * The super user Emails tab: every email the application sent, captured or failed to send, newest
 * first, searchable by recipient, template, outcome and day (#6922). A row opens the email in full.
 */
export default function EmailLog() {
  const [typed, setTyped] = useState(NO_FILTERS);
  const [filters, setFilters] = useState(NO_FILTERS);
  const [pagination, setPagination] = useState({
    pageIndex: PaginationParameters.DEFAULT_PAGE_INDEX,
    pageSize: PaginationParameters.DEFAULT_PAGE_SIZE,
  });
  const [openEntryId, setOpenEntryId] = useState<string | undefined>(undefined);

  // The same query object on every render while nothing changed: RTK Query compares a class
  // instance by reference, so one built during render would send a failed read again each render.
  const query = useMemo(
    () => ({
      pagination: PaginationParameters.create(pagination),
      recipient: filters.recipient,
      template: filters.template,
      outcome: filters.outcome || undefined,
      recordedFrom: startOfUtcDay(filters.fromDay),
      recordedBefore: startOfUtcDay(filters.toDay, 1),
    }),
    [filters, pagination],
  );
  const search = useSearchEmailLogQuery(query, {
    pollingInterval: EMAIL_LOG_POLLING_INTERVAL_MS,
    skipPollingIfUnfocused: true,
  });
  const entries: EmailLogEntrySummary[] = search.data?.data ?? [];
  const total = search.data?.headers.totalElements ?? 0;
  const filtered = Object.values(filters).some(Boolean);

  // Typing reaches the API once it pauses, and always from the first page.
  useEffect(() => {
    if (typed === filters) return;
    const timeout = setTimeout(() => {
      setFilters(typed);
      setPagination((current) => (current.pageIndex === 0 ? current : { ...current, pageIndex: 0 }));
    }, EMAIL_LOG_SEARCH_DELAY_MS);
    return () => clearTimeout(timeout);
  }, [typed, filters]);

  function change(field: keyof EmailLogFilters, value: string) {
    setTyped((current) => ({ ...current, [field]: value }));
  }

  return (
    <Page>
      <Typography variant="h6">Emails</Typography>
      <FilterBar>
        <TextField
          label="Recipient"
          size="small"
          value={typed.recipient}
          slotProps={{ htmlInput: { maxLength: EMAIL_LOG_SEARCH_MAXIMUM_LENGTH } }}
          onChange={(event) => change("recipient", event.target.value)}
        />
        <TextField
          label="Template"
          size="small"
          value={typed.template}
          slotProps={{ htmlInput: { maxLength: EMAIL_LOG_SEARCH_MAXIMUM_LENGTH } }}
          onChange={(event) => change("template", event.target.value)}
        />
        <FormControl size="small">
          <InputLabel id="email-log-outcome-label">Outcome</InputLabel>
          <Select
            labelId="email-log-outcome-label"
            label="Outcome"
            value={typed.outcome}
            onChange={(event) => change("outcome", event.target.value)}
          >
            <MenuItem value="">Any outcome</MenuItem>
            {OUTCOMES.map((outcome) => (
              <MenuItem key={outcome} value={outcome}>
                {outcome}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <TextField
          label="From (UTC)"
          type="date"
          size="small"
          value={typed.fromDay}
          slotProps={{ inputLabel: { shrink: true } }}
          onChange={(event) => change("fromDay", event.target.value)}
        />
        <TextField
          label="To (UTC)"
          type="date"
          size="small"
          value={typed.toDay}
          slotProps={{ inputLabel: { shrink: true } }}
          onChange={(event) => change("toDay", event.target.value)}
        />
      </FilterBar>
      {search.isError && <Alert severity="error">{searchErrorMessage(search.error)}</Alert>}
      {entries.some((entry) => entry.isCaptured) && <Alert severity="info">{EMAIL_LOG_CAPTURED_NOTICE}</Alert>}
      <TableContainer>
        <Table size="small" aria-label="Emails">
          <TableHead>
            <TableRow>
              <TableCell>Recorded</TableCell>
              <TableCell>Outcome</TableCell>
              <TableCell>Recipient</TableCell>
              <TableCell>Subject</TableCell>
              <TableCell>Template</TableCell>
              <TableCell>Environment</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {!search.isLoading && !search.isError && entries.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  <Typography color="text.secondary">
                    {filtered ? "No emails match these filters." : "No emails have been logged yet."}
                  </Typography>
                </TableCell>
              </TableRow>
            )}
            {entries.map((entry) => (
              <ClickableRow
                key={entry.id.value}
                hover
                tabIndex={0}
                onClick={() => setOpenEntryId(entry.id.value)}
                onKeyDown={(event) => {
                  if (event.key === "Enter" || event.key === " ") setOpenEntryId(entry.id.value);
                }}
              >
                <TableCell sx={{ whiteSpace: "nowrap" }}>{formatEmailLogInstant(entry.recordedAt)}</TableCell>
                <TableCell>
                  <Chip size="small" color={OUTCOME_COLORS[entry.outcome]} label={entry.outcome} />
                </TableCell>
                <TableCell>
                  {entry.recipient ?? "—"}
                  {entry.carbonCopy && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                      Cc {entry.carbonCopy}
                    </Typography>
                  )}
                </TableCell>
                <TableCell>{entry.subject ?? "—"}</TableCell>
                <TableCell>{entry.template ?? "—"}</TableCell>
                <TableCell>{entry.environment}</TableCell>
              </ClickableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <Box>
        <TablePagination
          component="div"
          count={total}
          page={pagination.pageIndex}
          rowsPerPage={pagination.pageSize}
          rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
          onPageChange={(_event, pageIndex) => setPagination((current) => ({ ...current, pageIndex }))}
          onRowsPerPageChange={(event) =>
            setPagination({ pageIndex: 0, pageSize: Number.parseInt(event.target.value, 10) })
          }
        />
      </Box>
      {openEntryId && <EmailLogEntryDialog entryId={openEntryId} onClose={() => setOpenEntryId(undefined)} />}
    </Page>
  );
}
